import React, { useEffect, useCallback, useRef, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../../../redux/store';
import { POPUP, MODAL } from '../../../../../../redux/types';
import { useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';

import useTranslate from '../../../../../../common/hooks/useTranslate';
import useExcelExportFormat from '../../../../../ui/table/hooks/useExcelExportFormat';
import useClickOutside from '../../../../../../common/hooks/useClickOutside';
import { getCashflowData } from '../../../../../services/analysisContentService';

import { useInternationalization } from '@progress/kendo-react-intl';
import { groupBy } from '@progress/kendo-data-query';
import { setExpandedState, setGroupIds } from '@progress/kendo-react-data-tools';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { ExcelExport } from '@progress/kendo-react-excel-export';

import AmountCell from '../cells/amountCell/amountCell';
import Icon from '../../../../../ui/icon/icon';
import ComponentLoader from '../../../../../ui/loaders/componentLoader/componentLoader';
import TextCell from '../../../../../ui/table/cells/textCell/textCell';
import DateCell from '../../../../../ui/table/cells/dateCell/dateCell';

import './cashFlowModal.scss';


const modalElement = document.getElementById('modal-root');

const initialGroup = [
  {
    field: 'objectName',
  },
];

const processWithGroups = (data, group) => {
  const newDataState = groupBy(data, group);
  setGroupIds({
    data: newDataState,
    group: group,
  });
  return newDataState;
};

export const CashFlowModal: React.FC = () => {
  const [displayData, setDisplayData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [measureInfo, setMeasureInfo] = useState<any>({});
  const [collapsedState, setCollapsedState] = useState<any[]>([]);

  const { t } = useTranslate();
  const intl = useInternationalization();
  const { investorId } = useParams();
  const dispatch = useDispatch();
  const modalRef = useRef(null);

  const showCashFlowModal = useSelector((state: RootState) => state.cashFlowModal.showModal);
  const data = useSelector((state: RootState) => state.cashFlowModal.data);
  const excelFormat = useExcelExportFormat();

  const closeDialog = () => {
    dispatch({ type: MODAL.SWITCH_CASH_FLOW_MODAL });
  };

  const handleEscape = useCallback(
    (event) => {
      if (event.keyCode === 27) closeDialog();
    },
    [closeDialog],
  );

  useEffect(() => {
    setLoading(true);
    if (data && data.cashFlowInfo) {
      getCashflowData(data.boardId, data.cashFlowInfo.nodeId, data.cashFlowInfo.valueId, investorId ? investorId : data.investorObjectId)
        .then((res) => {
          let dataForExport = res.data.map(item => ({...item, date: intl.formatDate(new Date(item.date), 'MM/dd/yyyy')}));
          setDisplayData(processWithGroups(dataForExport, initialGroup));
          if (data.cashFlowInfo.valueInfo) {
            setMeasureInfo(data.cashFlowInfo.valueInfo.measure);
          }
          setLoading(false);
        })
        .catch((err) => {
          dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message });
          closeDialog();
        });
    }
    if (showCashFlowModal) document.addEventListener('keydown', handleEscape, false);
    return () => {
      document.removeEventListener('keydown', handleEscape, false);
      setLoading(true);
      setDisplayData([]);
      setMeasureInfo({});
      setCollapsedState([]);
    };
  }, [data, showCashFlowModal]);

  useClickOutside(modalRef, closeDialog);

  const onExpandChange = React.useCallback(
    (event) => {
      const item = event.dataItem;
      if (item.groupId) {
        const newCollapsedIds = !event.value ? [...collapsedState, item.groupId] : collapsedState.filter((groupId) => groupId !== item.groupId);
        setCollapsedState(newCollapsedIds);
      }
    },
    [collapsedState],
  );

  const newData = setExpandedState({
    data: displayData,
    collapsedIds: collapsedState,
  });

  const FormattedAmountCell = (props) => {
    return <AmountCell {...props} measureInfo={measureInfo} />;
  };

  const _exportGrid = React.useRef(null);

  const gridExport = (): void => {
    // if (_exportGrid.current !== null) {
    //   _exportGrid.current.save();
    // }

    if (_exportGrid && _exportGrid.current) {
      const workbook = _exportGrid.current.workbookOptions();
      const rows = workbook?.sheets[0].rows;

      let rateIndex;
      rows?.forEach((row: any) => {
        if (row.type === 'header') {
          row.cells.forEach((cell: any, index: number) => {
            if (cell.value === 'Rate') {
              rateIndex = index;
            }
          })
        }
        if (row.type === 'data') {
          row.cells.forEach((cell: any, key: number) => {
            if (cell.value && typeof cell.value === 'number') {
              cell.format = excelFormat.numberFormat( 2);
            }
            if (rateIndex && key === rateIndex) {
              cell.format = undefined;
            }
          });
        }
      });

      _exportGrid.current.save(workbook);
    }
  };

  const amountTitle = () => {
    let currency = data.selectedCurrency && (data.selectedCurrency.currency === '_' ? data.dataItem.currency : `${data.selectedCurrency.currencySymbol}${data.selectedCurrency.currencyShortName}`);
    return t('BOARDS.GRID_FIELD.AMOUNT') + ' ' + currency;
  };

  const renderTitle = (data) => {
    let title: string[] = [];
    if (data && data.dataItem)
      title.push(data.dataItem.name || data.dataItem.periodName);
    if (data && data.columnName)
      title.push(`${data.columnName}:`);
    let valueInfo = data.cashFlowInfo?.valueInfo;
    let valueAmount = valueInfo?.amount;
    if (data && data.cashFlowInfo) {
      let formattedValue =
        valueAmount && typeof valueAmount === 'object'
          ? (data.cashflowInfo?.value?.lookupRef ? intl.formatNumber(valueAmount.rowAmount * data.cashflowInfo?.value?.lookupRef.amountsIn, (valueAmount.isPercent ? 'p' : 'n') + valueAmount.scale)
            : intl.formatNumber(valueAmount.rowAmount, (valueAmount.isPercent ? 'p' : 'n') + valueAmount.scale))
          : valueInfo.measure ? intl.formatNumber(valueAmount, (valueInfo.measure.isPercent ? 'p' : 'n') + parseInt(valueInfo.scale) || valueInfo.measure.scale) : valueAmount;
      title.push(formattedValue);
    }
    if (!data.value.includes('%') && data.selectedCurrency)
      title.push(`${data.selectedCurrency.currencySymbol || ''}${data.selectedCurrency.currencyShortName}`);
    if (!data.value.includes('%') && data.selectedCurrency && data.selectedCurrency.currency === '_')
      title.push(data.dataItem.currency);

    return title.join(' ');


  };

  return createPortal(
    showCashFlowModal ? (
      <div className={`modal-cash-flow`}>
        <div className='modal-body' ref={modalRef}>
          <div className={'modal-title'}>
            <div className='cashflow-header' title={renderTitle(data)}>
              {renderTitle(data)}
            </div>
            <div className='modal-title-buttons'>
              <Icon onClick={gridExport} name={'far fa-file-excel '}
                    className={'pointer export-icon export-icon hidden-mobile'} />
              <Icon name='fas fa-xmark' className='impersonate-off-icon pointer' onClick={closeDialog} />
            </div>
          </div>
          <div className='cashflow-grid-wrapper'>
            {!loading ? (
              <ExcelExport data={newData} ref={_exportGrid} group={initialGroup} fileName={t("CASHFLOW_DATA")}>
                <Grid data={newData} resizable={true} groupable={true} onExpandChange={onExpandChange}
                      group={initialGroup} expandField='expanded'>
                  <GridColumn
                    field='objectName'
                    title={t('BOARDS.GRID_FIELD.OBJECT_NAME')}
                    width={300}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='assignedObjectName'
                    title={t('BOARDS.GRID_FIELD.ASSIGNED_OBJECT_NAME')}
                    width={275}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn field='date' title={t('BOARDS.GRID_FIELD.DATE')} cell={(props) => <DateCell {...props} />}
                              width={150}
                              minResizableWidth={100} />
                  <GridColumn field='amount'
                              title={amountTitle()}
                              cell={FormattedAmountCell}
                              width={180} minResizableWidth={100} />
                  <GridColumn
                    field='eventName'
                    title={t('BOARDS.GRID_FIELD.EVENT_NAME')}
                    width={300}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='itemName'
                    title={t('BOARDS.GRID_FIELD.ITEM_NAME')}
                    width={300}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='typeName'
                    title={t('BOARDS.GRID_FIELD.TYPE_NAME')}
                    width={270}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='accountName'
                    title={t('BOARDS.GRID_FIELD.ACCOUNT_NAME')}
                    width={270}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='rate'
                    title={t('BOARDS.GRID_FIELD.RATE')}
                    width={150}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='sourceCurrency'
                    title={t('BOARDS.GRID_FIELD.SOURCE_CURRENCY')}
                    width={150}
                    minResizableWidth={100}
                    cell={(props) => <TextCell {...props} grid />}
                  />
                  <GridColumn
                    field='rateDate'
                    title={t('BOARDS.GRID_FIELD.RATE_DATE')}
                    width={150}
                    minResizableWidth={100}
                    cell={(props) => <DateCell {...props} />}
                  />
                </Grid>
              </ExcelExport>
            ) : (
              <div className='component-loader-wrapper'>
                <ComponentLoader />
              </div>
            )}
          </div>
        </div>
      </div>
    ) : null,
    modalElement,
  );
};

export default CashFlowModal;
